import { useEffect, useState } from "react";
import { Star, Images } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { wibNow } from "@/lib/time";
import { toast } from "sonner";

const MON = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MONTH_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const PERIODS = [["harian", "Harian"], ["mingguan", "Mingguan"], ["bulanan", "Bulanan"]];
const ATT_COLORS = { Hadir: "text-green-600", Izin: "text-sky-600", Sakit: "text-amber-600", Alpha: "text-red-500" };
const PREDIKAT_OPTS = ["Perlu Bimbingan", "Cukup", "Baik", "Sangat Baik"];

export function PerkembanganRekap({ studentId, canEdit = false }) {
  const now = wibNow();
  const [period, setPeriod] = useState("harian");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const year = now.getFullYear();
  const [monthly, setMonthly] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [aspectCat, setAspectCat] = useState([]);

  const load = () => {
    if (!studentId) return;
    api.get(`/reports/monthly?student_id=${studentId}&month=${month}&year=${year}`).then((r) => setMonthly(r.data));
    api.get(`/reports/weekly?student_id=${studentId}`).then((r) => setWeekly(r.data)).catch(() => {});
  };
  useEffect(load, [studentId, month, year]); // eslint-disable-line
  useEffect(() => { if (canEdit) api.get("/assessment/aspects").then((r) => setAspectCat(r.data)).catch(() => {}); }, [canEdit]);

  const predikat = monthly?.predikat;

  // ---- Penilaian (inline) ----
  const [af, setAf] = useState({ predikat: "Baik", note: "", aspects: [] });
  useEffect(() => {
    const names = predikat?.aspects?.length ? predikat.aspects : (aspectCat.length ? aspectCat.map((n) => ({ name: n, stars: 3 })) : []);
    setAf({ predikat: predikat?.predikat || "Baik", note: predikat?.note || "", aspects: names.map((a) => ({ name: a.name, stars: a.stars || 3 })) });
  }, [monthly, aspectCat]); // eslint-disable-line
  const saveAssess = async () => {
    try {
      await api.post("/assessments", { student_id: studentId, period: monthly?.label || `${MONTH_FULL[month - 1]} ${year}`,
        predikat: af.predikat, aspects: af.aspects, note: af.note || null });
      toast.success("Penilaian disimpan"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {/* period toggle */}
      <div className="flex gap-2 mb-4">
        {PERIODS.map(([v, l]) => (
          <button key={v} data-testid={`perk-period-${v}`} onClick={() => setPeriod(v)}
            className={`flex-1 py-2 rounded-full text-sm font-semibold ${period === v ? "bg-primary text-white shadow" : "bg-white border border-stone-200 text-stone-500"}`}>{l}</button>
        ))}
      </div>

      {period === "bulanan" && (
        <>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-3">
            {MON.map((m, i) => (
              <button key={m} data-testid={`perk-bulan-${i + 1}`} onClick={() => setMonth(i + 1)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold ${month === i + 1 ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>{m}</button>
            ))}
          </div>
          <AttendanceCard title={`Kehadiran ${monthly?.label || ""}`} counts={monthly?.attendance} pct={monthly?.attendance?.hadir_pct} />
        </>
      )}

      {period === "mingguan" && (
        <AttendanceCard title="Kehadiran Pekan Ini" counts={weekly?.attendance}
          pct={weekly?.attendance ? Math.round((weekly.attendance.Hadir / (weekly.attendance.total_recorded || 1)) * 100) : 0} />
      )}

      {period === "harian" && (
        <div className="mb-4 bg-secondary/10 border border-secondary/25 rounded-2xl px-4 py-3 text-xs text-stone-500" data-testid="perk-harian-hint">
          Penilaian perkembangan (predikat & aspek) diisi di bawah. Laporan harian dapat dilihat di tab <b className="text-stone-700">Absensi</b> & <b className="text-stone-700">Laporan</b>.
        </div>
      )}

      {/* Predikat */}
      {canEdit ? (
        <div className="mb-5 bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4" data-testid="perk-assess-form">
          <p className="text-xs text-stone-500 mb-1">Predikat</p>
          <select data-testid="assess-predikat-input" value={af.predikat} onChange={(e) => setAf({ ...af, predikat: e.target.value })}
            className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary">
            {PREDIKAT_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <p className="text-xs text-stone-500 mb-2">Aspek Perkembangan</p>
          <div className="space-y-2 mb-3">
            {af.aspects.map((asp, idx) => (
              <div key={idx} className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2">
                <span className="text-sm text-stone-700 flex-1 min-w-0 truncate pr-2">{asp.name}</span>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`assess-star-${idx}-${n}`} onClick={() => setAf({ ...af, aspects: af.aspects.map((a, i) => i === idx ? { ...a, stars: n } : a) })}>
                      <Star size={20} weight="fill" className={n <= asp.stars ? "text-secondary" : "text-stone-200"} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {af.aspects.length === 0 && <p className="text-xs text-stone-400">Aspek belum tersedia.</p>}
          </div>
          <p className="text-xs text-stone-500 mb-1">Catatan Guru</p>
          <textarea data-testid="assess-note-input" value={af.note} onChange={(e) => setAf({ ...af, note: e.target.value })} rows={3}
            className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary" />
          <button data-testid="assess-save-btn" onClick={saveAssess}
            className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan Penilaian</button>
        </div>
      ) : predikat ? (
        <div className="mb-5" data-testid="perk-predikat">
          <div className="bg-secondary/15 border border-secondary/30 rounded-3xl p-4 mb-3 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-secondary/30 flex items-center justify-center shrink-0">
              <Star size={30} weight="fill" className="text-secondary" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Predikat</p>
              <p className="font-heading text-xl font-bold text-stone-800">{predikat.predikat}</p>
              <p className="text-stone-500 text-xs">{predikat.period}</p>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm divide-y divide-stone-100 overflow-hidden">
            {(predikat.aspects || []).map((asp, i) => (
              <div key={i} data-testid={`perk-aspect-${i}`} className="flex items-center justify-between px-4 py-3">
                <span className="text-stone-700 text-sm">{asp.name}</span>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={16} weight="fill" className={n <= asp.stars ? "text-secondary" : "text-stone-200"} />)}
                </div>
              </div>
            ))}
          </div>
          {predikat.note && (
            <div className="mt-3 bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm">
              <p className="text-xs text-stone-400 mb-1">Catatan Guru</p>
              <p className="text-stone-600 text-sm leading-relaxed">{predikat.note}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-center text-stone-400 my-5 text-sm">Belum ada penilaian perkembangan.</p>
      )}

      {/* Foto kegiatan */}
      {monthly?.gallery?.length > 0 && (
        <div>
          <p className="font-heading font-semibold text-stone-800 mb-2 flex items-center gap-2"><Images size={18} weight="duotone" className="text-primary" /> Foto Kegiatan</p>
          <div className="grid grid-cols-3 gap-2" data-testid="perk-gallery">
            {monthly.gallery.map((url, i) => (
              <img key={i} src={url} alt="Kegiatan" className="w-full aspect-square object-cover rounded-xl border border-stone-200/60" />
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

function AttendanceCard({ title, counts, pct }) {
  const c = counts || { Hadir: 0, Izin: 0, Sakit: 0, Alpha: 0 };
  return (
    <div className="bg-white rounded-3xl border border-stone-200/60 p-4 shadow-sm mb-5" data-testid="perk-attendance">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-semibold text-stone-800 text-sm">{title}</p>
        <span className="text-sm font-bold text-primary">{pct || 0}% hadir</span>
      </div>
      <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden mb-3">
        <div className="h-full bg-primary rounded-full" style={{ width: `${pct || 0}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {["Hadir", "Izin", "Sakit", "Alpha"].map((k) => (
          <div key={k} className="bg-stone-50 rounded-xl p-2 text-center">
            <p className={`font-heading font-bold text-xl ${ATT_COLORS[k]}`}>{c[k] || 0}</p>
            <p className="text-[10px] text-stone-400">{k}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
